import React from 'react';
import { createPortal } from 'react-dom';
import { Drawer } from 'vaul';
import { X } from 'lucide-react';
import { useIsDesktop } from '@/hooks/useMediaQuery';

interface ResponsiveDrawerProps {
  /** Whether the drawer/sheet is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Drawer/sheet content */
  children: React.ReactNode;
  /** Title displayed in the header */
  title?: string;
  /** Custom header content (replaces default title) */
  header?: React.ReactNode;
  /** Footer content (sticky at bottom) */
  footer?: React.ReactNode;
  /** Width of the desktop sheet (default: 458px) */
  sheetWidth?: number;
  /** Whether this is a nested drawer (for mobile) */
  nested?: boolean;
  /** z-index for the overlay and content */
  zIndex?: number;
  /** Whether to reposition inputs on mobile (vaul feature) */
  repositionInputs?: boolean;
}

/**
 * Responsive drawer component that renders:
 * - Bottom sheet (vaul Drawer) on mobile
 * - Right-side sheet on desktop
 * 
 * This allows reusing the same drawer content across both layouts
 * while providing the appropriate UX for each.
 */
export const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
  open,
  onOpenChange,
  children,
  title,
  header,
  footer,
  sheetWidth = 458,
  nested = false,
  zIndex = 100,
  repositionInputs = false,
}) => {
  const isDesktop = useIsDesktop();

  // Desktop: Right-side sheet
  if (isDesktop) {
    if (!open) return null;

    const desktopSheet = (
      <>
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/60 transition-opacity duration-300 animate-in fade-in"
          style={{ zIndex }}
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />

        {/* Sheet */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'sheet-title' : undefined}
          className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl animate-in slide-in-from-right duration-300"
          style={{
            width: `${sheetWidth}px`,
            zIndex: zIndex + 1,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-secondary sticky top-0 z-10">
            {header || (
              <div>
                {title && (
                  <h2 id="sheet-title" className="text-lg font-semibold text-text-primary">
                    {title}
                  </h2>
                )}
              </div>
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="sticky bottom-0 bg-secondary border-t border-border p-4">
              {footer}
            </div>
          )}
        </div>
      </>
    );

    return createPortal(desktopSheet, document.body);
  }

  // Mobile: Bottom sheet (vaul Drawer)
  const DrawerRoot = nested ? Drawer.NestedRoot : Drawer.Root;

  return (
    <DrawerRoot open={open} onOpenChange={onOpenChange} repositionInputs={repositionInputs}>
      <Drawer.Portal>
        <Drawer.Overlay 
          className="fixed inset-0 bg-black/80" 
          style={{ zIndex }} 
        />
        <Drawer.Content 
          className="bg-secondary flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[90vh]"
          style={{ zIndex: zIndex + 1 }}
        >
          {/* Header */}
          <div className="mx-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            {header || (
              title && (
                <h2 className="text-xs text-text-primary text-center">{title}</h2>
              )
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="sticky bottom-0 bg-secondary border-t border-gray-500/30 p-4">
              {footer}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </DrawerRoot>
  );
};
